import {
    ALL_EMAIL_FREQUENCY_OF_REMINDERS,
    EMAIL_FREQUENCY_OF_REMINDER_LABELS,
} from '@common/enums';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    type Dispatch,
    forwardRef,
    type SetStateAction,
    useImperativeHandle,
    useState,
} from 'react';
import LexoSwitch from '@/components/LexoSwitch';
import EmailTemplatePreview from '@/pages/QuoteIntake/EmailTemplatePreview';
import { createEmailTemplate, updateEmailTemplate } from '@/services/EmailService';
import { updateQuoteIntakeTemplateStatus } from '@/services/QuoteTemplatesService';
import { errorSnackbar, infoSnackbar } from '@/utils/snackbar';
import { REMINDER_TIME_OPTIONS } from '../constants';
import type {
    IntakeDesignData,
    IntakeEmailErrors,
    IntakeReminderEmailData,
    MatterTypeData,
} from './types';

interface Props {
    quoteTemplateId: number | null;
    intakeReminder: IntakeReminderEmailData;
    setIntakeReminder: Dispatch<SetStateAction<IntakeReminderEmailData>>;
    matterType: MatterTypeData;
    intakeDesign: IntakeDesignData;
    lawyerSignature: string;
}

const IntakeReminderEmailSetup = forwardRef(function IntakeReminderEmailSetup(
    {
        quoteTemplateId,
        intakeReminder,
        setIntakeReminder,
        matterType,
        intakeDesign,
        lawyerSignature,
    }: Props,
    ref,
) {
    const theme = useTheme();
    const [isDirty, setIsDirty] = useState(false);
    const [errors, setErrors] = useState<IntakeEmailErrors>();

    const submit = async (): Promise<{ success: boolean }> => {
        if (!quoteTemplateId) return { success: false };

        // Validation
        const { reminderSubject, reminderBodyBeforeLink, reminderBodyAfterLink } = intakeReminder;
        if (
            reminderSubject.length === 0 ||
            reminderBodyBeforeLink.length === 0 ||
            reminderBodyAfterLink.length === 0
        ) {
            setErrors({
                emailSubject: reminderSubject.length === 0 ? 'Subject is required' : undefined,
                emailBodyIntroduction:
                    reminderBodyBeforeLink.length === 0
                        ? 'Email body introduction is required'
                        : undefined,
                emailBodyClosing:
                    reminderBodyAfterLink.length === 0
                        ? 'Email body closing is required'
                        : undefined,
            });
            errorSnackbar('Please fill all required fields', true);
            return { success: false };
        }

        const createOrUpdatePayload = {
            emailTemplateType: 'IntakeEmailReminderTemplate' as const,
            isQuoteApprovalEnable: false,
            isQuoteReminderOn: intakeReminder.isQuoteReminderOn,
            emailSubject: intakeReminder.reminderSubject,
            emailBodyIntroduction: intakeReminder.reminderBodyBeforeLink,
            emailBodyClosing: intakeReminder.reminderBodyAfterLink,
            emailCC: matterType.emailCC?.length > 0 ? matterType.emailCC : null,
            emailReminder: {
                emailFrequencyOfReminder: intakeReminder.emailFrequencyOfReminder,
                reminderTime: intakeReminder.reminderTime,
                totalNumberOfReminders: intakeReminder.totalNumberOfReminders,
            },
            emailSignature: lawyerSignature,
            enableReminderExhaustNotification:
                intakeReminder.enableReminderExhaustNotification || false,
        };
        try {
            if (intakeReminder.intakeReminderEmailTemplateId) {
                if (isDirty) {
                    await updateEmailTemplate(
                        quoteTemplateId,
                        intakeReminder.intakeReminderEmailTemplateId,
                        createOrUpdatePayload,
                    );
                }
            } else {
                const response = await createEmailTemplate(quoteTemplateId, createOrUpdatePayload);
                setIntakeReminder((prev) => ({
                    ...prev,
                    intakeReminderEmailTemplateId: response.data.emailTemplateId,
                }));
            }
            if (intakeDesign.templateFormId) {
                await updateQuoteIntakeTemplateStatus(
                    quoteTemplateId,
                    intakeDesign.templateFormId,
                    'Completed',
                );
            }
            return { success: true };
        } catch (error: unknown) {
            console.error(error);
            errorSnackbar('Something went wrong!');
            return { success: false };
        }
    };

    useImperativeHandle(ref, () => ({ submit }));

    const handleStateChange = (
        event: { target: { value: string | number | boolean } },
        key: string,
    ) => {
        setIsDirty(true);
        setErrors(undefined);
        setIntakeReminder((prev) => ({
            ...prev,
            [key]: event.target.value,
        }));
    };

    return (
        <Stack
            padding={3}
            spacing={3}
            sx={{
                maxHeight: 'calc(100vh - 168px)',
                overflow: 'auto',
            }}
        >
            <Stack direction="row" spacing={3}>
                <Stack
                    spacing={3}
                    sx={{
                        borderRadius: '8px',
                        border: `1px solid ${theme.palette.neutral.borderColor}`,
                        backgroundColor: 'neutral.white',
                        padding: '24px',
                        height: 'fit-content',
                        width: '100%',
                        margin: '32px 0px 32px 32px',
                    }}
                >
                    <Stack spacing={1}>
                        <Typography variant="h2">Intake email reminder template</Typography>
                        <Typography variant="body2">
                            When your client accepts a quote, they will be automatically directed to
                            the intake form. An email will be sent to them after acceptance,
                            according to the reminder schedule you have set for this email.
                        </Typography>
                    </Stack>
                    <Divider orientation="horizontal" />

                    <Stack spacing={2}>
                        <Typography variant="body2">Enable intake reminders</Typography>
                        <LexoSwitch
                            checked={intakeReminder.isQuoteReminderOn}
                            onChange={(event) => {
                                const ev = { target: { value: event.target.checked } };
                                if (intakeReminder.isQuoteReminderOn) {
                                    infoSnackbar('Reminders are currently set to off');
                                }
                                handleStateChange(ev, 'isQuoteReminderOn');
                            }}
                        />
                        <Accordion expanded={intakeReminder.isQuoteReminderOn}>
                            <AccordionSummary>
                                <Typography
                                    variant={'labelLarge'}
                                    color={
                                        intakeReminder.isQuoteReminderOn
                                            ? theme.palette.neutral.black
                                            : theme.palette.neutral.gray
                                    }
                                >
                                    Reminders frequency
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails
                                sx={{ '&.MuiAccordionDetails-root': { paddingTop: '0px' } }}
                            >
                                <Stack spacing={2}>
                                    <Divider orientation="horizontal" />
                                    <FormControl fullWidth>
                                        <InputLabel id="reminder-count-label">
                                            Total number of intake reminders
                                        </InputLabel>
                                        <Select
                                            value={intakeReminder.totalNumberOfReminders}
                                            onChange={(event) =>
                                                handleStateChange(event, 'totalNumberOfReminders')
                                            }
                                            disabled={!intakeReminder.isQuoteReminderOn}
                                            labelId="reminder-count-label"
                                            id="reminder-total-count"
                                            label="Total number of intake reminders"
                                        >
                                            <MenuItem value={1}>1</MenuItem>
                                            <MenuItem value={2}>2</MenuItem>
                                            <MenuItem value={3}>3</MenuItem>
                                            <MenuItem value={4}>4</MenuItem>
                                            <MenuItem value={5}>5</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth>
                                        <InputLabel id="reminder-frequency-label">
                                            Frequency of intake reminders
                                        </InputLabel>
                                        <Select
                                            value={intakeReminder.emailFrequencyOfReminder}
                                            onChange={(event) =>
                                                handleStateChange(event, 'emailFrequencyOfReminder')
                                            }
                                            disabled={!intakeReminder.isQuoteReminderOn}
                                            labelId="reminder-frequency-label"
                                            label="Frequency of intake reminders"
                                        >
                                            {ALL_EMAIL_FREQUENCY_OF_REMINDERS.map((frequency) => (
                                                <MenuItem key={frequency} value={frequency}>
                                                    {EMAIL_FREQUENCY_OF_REMINDER_LABELS[frequency]}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth>
                                        <InputLabel id="reminder-time-label">
                                            Reminders time
                                        </InputLabel>
                                        <Select
                                            value={intakeReminder.reminderTime}
                                            onChange={(event) =>
                                                handleStateChange(event, 'reminderTime')
                                            }
                                            disabled={!intakeReminder.isQuoteReminderOn}
                                            labelId="reminder-time-label"
                                            id="reminder-time"
                                            label="Reminders time"
                                        >
                                            {Object.entries(REMINDER_TIME_OPTIONS).map(
                                                ([key, value]) => (
                                                    <MenuItem key={key} value={value}>
                                                        {key}
                                                    </MenuItem>
                                                ),
                                            )}
                                        </Select>
                                    </FormControl>
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                        <Divider orientation="horizontal" />
                        <Typography variant="body2">
                            Notify via email when the maximum number of reminders has been sent to
                            the client.
                        </Typography>
                        <LexoSwitch
                            checked={intakeReminder.enableReminderExhaustNotification}
                            onChange={(event) => {
                                const ev = { target: { value: event.target.checked } };
                                handleStateChange(ev, 'enableReminderExhaustNotification');
                            }}
                        />
                    </Stack>
                    <Divider orientation="horizontal" />
                    <Stack spacing={4}>
                        <TextField
                            value={intakeReminder.reminderSubject}
                            onChange={(ev) => handleStateChange(ev, ev.target.name)}
                            fullWidth
                            placeholder="Intake Email Reminder"
                            name={'reminderSubject'}
                            error={!!errors?.emailSubject}
                            helperText={errors?.emailSubject}
                            type="text"
                            variant="outlined"
                            label="Subject"
                        />
                        <TextField
                            value={intakeReminder.reminderBodyBeforeLink}
                            onChange={(ev) => handleStateChange(ev, ev.target.name)}
                            name={'reminderBodyBeforeLink'}
                            fullWidth
                            placeholder=""
                            type="text"
                            multiline={true}
                            rows={4}
                            error={!!errors?.emailBodyIntroduction}
                            helperText={
                                errors?.emailBodyIntroduction
                                    ? 'Email body introduction is required.'
                                    : ''
                            }
                            variant="outlined"
                            label="First part of body (before form link)"
                        />
                        <TextField
                            value={intakeReminder.reminderBodyAfterLink}
                            onChange={(ev) => handleStateChange(ev, ev.target.name)}
                            name={'reminderBodyAfterLink'}
                            fullWidth
                            placeholder=""
                            type="text"
                            multiline={true}
                            rows={4}
                            error={!!errors?.emailBodyClosing}
                            helperText={
                                errors?.emailBodyClosing ? 'Email body closing is required.' : ''
                            }
                            variant="outlined"
                            label="Second part of body (after form link)"
                        />
                    </Stack>
                </Stack>
                <EmailTemplatePreview
                    subject={intakeReminder?.reminderSubject}
                    bodyBeforeLink={intakeReminder?.reminderBodyBeforeLink}
                    bodyAfterLink={intakeReminder?.reminderBodyAfterLink}
                    signature={lawyerSignature}
                />
            </Stack>
        </Stack>
    );
});

export default IntakeReminderEmailSetup;
